import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Locale } from "@anyseal/contracts";

const messages = {
  "zh-CN": {
    brand: "LonMax AI", portal: "开发者门户", overview: "概览", credentials: "API 密钥", models: "模型目录", usage: "请求与用量", billing: "账单与额度", growth: "奖励与兑换", docs: "开发文档", security: "账户与安全", legal: "法律与隐私", signOut: "退出登录", language: "切换语言", loading: "正在加载", retry: "重新加载", requestId: "请求 ID", organization: "组织", project: "项目", currentWorkspace: "当前工作区", statusHealthy: "服务运行正常", menu: "打开菜单", close: "关闭",
  },
  en: {
    brand: "LonMax AI", portal: "Developer Portal", overview: "Overview", credentials: "API keys", models: "Models", usage: "Requests & usage", billing: "Billing & credit", growth: "Rewards & redemption", docs: "Developer docs", security: "Account & security", legal: "Legal & privacy", signOut: "Sign out", language: "Language", loading: "Loading", retry: "Reload", requestId: "Request ID", organization: "Organization", project: "Project", currentWorkspace: "Current workspace", statusHealthy: "Service operational", menu: "Open menu", close: "Close",
  },
} as const;

type Key = keyof typeof messages["zh-CN"];
type I18nValue = { locale: Locale; setLocale: (locale: Locale) => void; t: (key: Key) => string };
const I18nContext = createContext<I18nValue | null>(null);

const initialLocale = (): Locale => {
  const saved = localStorage.getItem("anyseal.portal.locale");
  if (saved === "zh-CN" || saved === "en") return saved;
  return navigator.language.toLowerCase().startsWith("zh") ? "zh-CN" : "en";
};

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const setLocale = (next: Locale) => { localStorage.setItem("anyseal.portal.locale", next); setLocaleState(next); };
  useEffect(() => { document.documentElement.lang = locale; }, [locale]);
  const value = useMemo<I18nValue>(() => ({ locale, setLocale, t: (key) => messages[locale][key] }), [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("I18nProvider is missing");
  return value;
}
